import {
  faArrowDownToLine,
  faCircleNotch,
  faQuestionCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper } from "@tanstack/react-table";

import { useAllOrganizationUsers } from "src/api/taktile/queries";
import { useWorkspaceApiKeys } from "src/apiKeys/api/queries";
import { PAGE_SIZE } from "src/auditLogs/AuditLogsPage";
import { AuditEvent } from "src/auditLogs/queries";
import { downloadEventAsCSV } from "src/auditLogs/utils";
import { ColumnFilter } from "src/base-components/ColumnFilter";
import { Pill } from "src/base-components/Pill";
import { TableComp as Table } from "src/base-components/Table";
import { Icon, IconButton } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { CellWrapper, ColumnHeader } from "src/jobs/common/TableElements";
import { formatDate } from "src/utils/datetime";
import { useParamsDecode } from "src/utils/useParamsDecode";

const helper = createColumnHelper<AuditEvent>();

type AuditLogActor = {
  id: string;
  name: string;
  type: "user" | "api_key";
};

type GetColumnsArgs = {
  actors: AuditLogActor[] | null;
  onActorSelect: (id: string | null) => void;
  selectedActorId: string | null;
};

const getColumns = ({
  actors,
  selectedActorId,
  onActorSelect,
}: GetColumnsArgs) => {
  return [
    helper.accessor("created_at", {
      header: () => <ColumnHeader>Time</ColumnHeader>,
      cell: (info) => (
        <CellWrapper>
          <div className="w-36">
            {formatDate(info.getValue(), "MMM d, yyyy 'at' h:mmaaa")}
          </div>
        </CellWrapper>
      ),
    }),
    helper.accessor("actor_type", {
      header: () => <ColumnHeader>Actor type </ColumnHeader>,
      cell: (info) => (
        <CellWrapper>
          <div className="w-20">
            <Pill
              size="sm"
              variant={info.getValue() === "user" ? "indigo-light" : "white"}
            >
              <Pill.Text>
                {info.getValue() === "user" ? "User" : "API key"}
              </Pill.Text>
            </Pill>
          </div>
        </CellWrapper>
      ),
    }),
    helper.accessor("actor_id", {
      header: () => <ColumnHeader>Actor ID</ColumnHeader>,
      cell: (info) => {
        return (
          <CellWrapper>
            <div className="w-80">{info.getValue()}</div>
          </CellWrapper>
        );
      },
    }),
    helper.accessor("actor_name", {
      header: () => (
        <ColumnHeader>
          <span>Actor name</span>
          <span className="ml-1">
            {actors ? (
              <ColumnFilter
                description="Filter by Actor name"
                elements={actors.map((actor) => ({
                  key: actor.id,
                  value: actor.name,
                }))}
                isFiltering={selectedActorId !== null}
                placement="bottom start"
                selected={selectedActorId ?? undefined}
                onResetRequest={() => onActorSelect(null)}
                onSelect={onActorSelect}
              />
            ) : (
              <Icon icon={faCircleNotch} size="xs" spin />
            )}
          </span>
        </ColumnHeader>
      ),
      cell: (info) => {
        return (
          <CellWrapper>
            <div className="w-48 truncate">{info.getValue()}</div>
          </CellWrapper>
        );
      },
    }),
    helper.accessor("action", {
      header: () => <ColumnHeader>Event</ColumnHeader>,
      cell: (info) => {
        return (
          <CellWrapper>
            <div className="w-20 truncate">
              <Pill size="sm" variant="gray">
                <Pill.Text>{info.getValue()}</Pill.Text>
              </Pill>
            </div>
          </CellWrapper>
        );
      },
    }),

    helper.accessor("entity_type", {
      header: () => <ColumnHeader>Entity type</ColumnHeader>,
      cell: (info) => {
        return (
          <CellWrapper>
            <div className="w-32 truncate">
              <Pill size="sm" variant="outlined-white">
                <Pill.Text>{info.getValue()}</Pill.Text>
              </Pill>
            </div>
          </CellWrapper>
        );
      },
    }),
    helper.accessor("entity_id", {
      header: () => <ColumnHeader>Target entity ID</ColumnHeader>,
      cell: (info) => {
        return (
          <CellWrapper>
            <div className="w-66 truncate">{info.getValue()}</div>
          </CellWrapper>
        );
      },
    }),
    helper.accessor("auth_phase", {
      header: () => <ColumnHeader>Auth phase</ColumnHeader>,
      cell: (info) => {
        const tooltipBody = (
          <div className="min-w-[268px] space-y-4">
            {Object.entries(info.row.original.details ?? []).map(
              ([key, value]) => (
                <div key={key}>
                  <p className="font-inter-semibold-13px">{key}</p>
                  <p className="font-inter-normal-12px text-gray-300">
                    {value !== undefined && typeof value === "object"
                      ? JSON.stringify(value)
                      : String(value)}
                  </p>
                </div>
              ),
            )}
          </div>
        );
        return (
          <CellWrapper>
            <div className="flex w-32 flex-1 items-center">
              <Pill
                size="sm"
                variant={info.getValue() === "authorized" ? "green" : "yellow"}
              >
                <Pill.Text>{info.getValue()}</Pill.Text>
              </Pill>
              <div className="invisible ml-auto flex gap-2 group-hover:visible">
                <Tooltip
                  body={tooltipBody}
                  footerAction={{
                    text: "Download event",
                    onClick: () => downloadEventAsCSV(info.row.original),
                  }}
                  title="Event details"
                >
                  <Icon icon={faQuestionCircle} size="xs" />
                </Tooltip>
                <IconButton
                  icon={faArrowDownToLine}
                  size="xs"
                  onClick={() => downloadEventAsCSV(info.row.original)}
                />
              </div>
            </div>
          </CellWrapper>
        );
      },
    }),
  ];
};

export const AuditLogsTable: React.FC<
  {
    data: AuditEvent[];
    isLoading: boolean;
    onScrolledToTheBottom: () => void;
    isFetchingNextPage: boolean;
  } & Omit<GetColumnsArgs, "actors">
> = ({
  data,
  isLoading,
  onScrolledToTheBottom,
  isFetchingNextPage,
  ...getColumnsArgs
}) => {
  const { orgId, wsId } = useParamsDecode<{ orgId: string; wsId: string }>();

  const { data: users } = useAllOrganizationUsers(orgId);
  const { data: apiKeys } = useWorkspaceApiKeys(orgId, wsId);

  const userActors: AuditLogActor[] | null =
    users?.pages.flatMap((page) =>
      page.map(({ id, email }) => ({ id, name: email ?? "", type: "user" })),
    ) ?? null;
  const apiKeyActors: AuditLogActor[] | null =
    apiKeys?.map(({ id, name }) => ({ id, name, type: "api_key" })) ?? null;

  const actors =
    userActors && apiKeyActors
      ? [...userActors, ...apiKeyActors]
          .sort((a, b) => a.name.localeCompare(b.name))
          // Filter out blank names
          .filter((actor) => !!actor.name)
      : null;

  return (
    <Table
      columns={getColumns({ ...getColumnsArgs, actors })}
      data={data}
      frameClassName="w-full pb-2"
      getRowId={(row) => row.id}
      isFetchingNextPage={isFetchingNextPage}
      isLoading={isLoading}
      loadingRowsCount={PAGE_SIZE}
      onScrolledToTheBottom={onScrolledToTheBottom}
    />
  );
};
